import * as React from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import {
  ServiceType,
  ServiceTypeResponse,
} from '../../types/ServiceAccordion.type';
import { z } from 'zod';
import { notifyAPIClient, notifyFailure } from '../../utils/toast';
import { apiPost, apiPut } from '../../utils/apiClient';

type ServiceTypeDialogProps = {
  open: boolean;
  handleClose: () => void;
  serviceType: ServiceType | null;
  fetchServiceTypes: () => void;
};

const ServiceTypeInput = z.object({
  id: z.number().int().optional(),
  name: z
    .string()
    .min(1, 'Service name is required')
    .refine((val) => val.length === 0 || val.trim().length > 0, {
      message: 'Service name cannot be just whitespace',
    }),
});
type ServiceTypeInputType = z.infer<typeof ServiceTypeInput>;

const ServiceTypeDialog = ({
  open,
  handleClose,
  serviceType,
  fetchServiceTypes,
}: ServiceTypeDialogProps) => {
  const [formError, setFormError] = React.useState<string | null>(null);

  const handleConfirm = async (data: ServiceTypeInputType) => {
    try {
      if (serviceType) {
        // Update existing service type
        const updateRes = await apiPut<
          ServiceTypeResponse,
          ServiceTypeInputType
        >('/service/update', data);
        notifyAPIClient(updateRes.message);
      } else {
        // Create new service type
        const createRes = await apiPost<
          ServiceTypeResponse,
          ServiceTypeInputType
        >('/service/create', data);
        notifyAPIClient(createRes.message);
      }
      fetchServiceTypes();
      handleClose();
    } catch (error) {
      notifyFailure();
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    try {
      const data = ServiceTypeInput.parse({
        id: serviceType?.id || 0,
        name: formData.get('name'),
      });
      setFormError(null);
      handleConfirm(data);
    } catch (error) {
      if (error instanceof z.ZodError) {
        setFormError(error.errors[0].message);
      } else {
        console.error(error);
      }
    }
  };

  return (
    <Dialog fullWidth open={open} onClose={handleClose}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Add new service</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            name="name"
            className="required-field"
            label="Service Name"
            type="text"
            fullWidth
            variant="standard"
            defaultValue={serviceType?.name || ''}
            error={Boolean(formError)}
            helperText={formError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit">Confirm</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ServiceTypeDialog;
